import React, { useState } from 'react';
import { useConfirm } from '../contexts/ConfirmContext';
import { guestIdentityKey } from '../hooks/useApplicantStats';

const PRIORITY_OPTIONS = [
  { value: -1, label: '最低',    desc: '空きが残った時だけ最後に当選（最後尾）' },
  { value: 0,  label: '通常',    desc: '通常のマッチング' },
  { value: 1,  label: '優先保証', desc: '第1希望のキャストと優先的にマッチ' },
  { value: 2,  label: '優先 2',  desc: '競合時に優先される' },
  { value: 3,  label: '優先 3',  desc: '競合時に優先される' },
];

/** 直近の当選から何日前か（カレンダー日付ベース） */
const daysAgo = (ts) => {
  if (!ts) return null;
  const now = new Date();
  const then = new Date(ts);
  const nowDay  = new Date(now.getFullYear(),  now.getMonth(),  now.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const diff = Math.round((nowDay - thenDay) / (24 * 60 * 60 * 1000));
  return diff < 0 ? 0 : diff;
};

const STATUS_STYLE = {
  waiting: { label: '待機中',   bg: '#fffbeb', color: '#92400e', dot: '#f59e0b' },
  matched: { label: 'マッチ済', bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
};

const GuestCard = ({ guestId, guest, onRemove, onUpdatePriority, onOpenPicker, showPicker, confirm, stat, canViewHistory }) => {
  const [hovPicker,    setHovPicker]    = useState(false);
  const [hovDel,       setHovDel]       = useState(false);
  const [hovDetail,    setHovDetail]    = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);

  const status   = STATUS_STYLE[guest.status] || STATUS_STYLE.waiting;
  const priority = guest.priority || 0;
  const picks    = guest.draft_picks || [];
  const hasDetail = guest.x_url || guest.vrchat_profile || guest.profile_text;

  return (
    <div style={{
      borderRadius: '12px',
      border: '1px solid #e8edf5',
      background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      borderLeft: `3px solid ${guest.status === 'matched' ? '#10b981' : '#e2e8f0'}`,
      overflow: 'hidden',
    }}>
      {/* ── Main row ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px' }}>

        {/* Avatar */}
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
          background: '#10b981', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: '800', fontSize: '15px', overflow: 'hidden',
        }}>
          {guest.image_url
            ? <img src={guest.image_url} alt={guest.vrchat_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : guest.vrchat_name.charAt(0).toUpperCase()
          }
        </div>

        {/* Name + badges */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {guest.vrchat_name}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            {priority >= 1 && (
              <span style={{
                background: '#fef3c7', color: '#92400e',
                borderRadius: '4px', padding: '1px 6px',
                fontSize: '10px', fontWeight: '700',
              }}>
                ⭐ {PRIORITY_OPTIONS.find(o => o.value === priority)?.label}
              </span>
            )}
            {priority < 0 && (
              <span style={{
                background: '#f1f5f9', color: '#64748b',
                borderRadius: '4px', padding: '1px 6px',
                fontSize: '10px', fontWeight: '700',
              }}>
                ▽ 最低
              </span>
            )}
            {/* 当選回数・前回当選（Proのみ表示） */}
            {canViewHistory && (stat?.won > 0) && (
              <span style={{
                background: '#ede9fe', color: '#6c63ff',
                borderRadius: '4px', padding: '1px 6px',
                fontSize: '10px', fontWeight: '700',
              }}>
                🏆 当選{stat.won}回
                {daysAgo(stat.last_won_at) != null && (
                  <span style={{ marginLeft: 4, color: '#8b5cf6', fontWeight: 600 }}>
                    （前回{daysAgo(stat.last_won_at) === 0 ? '今日' : `${daysAgo(stat.last_won_at)}日前`}）
                  </span>
                )}
              </span>
            )}
            {picks.length > 0 && (
              <span style={{
                background: '#d1fae5', color: '#065f46',
                borderRadius: '4px', padding: '1px 6px',
                fontSize: '10px', fontWeight: '700',
              }}>
                🎯 指名 {picks.length}件
              </span>
            )}
            {guest.x_url && (
              <span style={{ fontSize: '10px', color: '#94a3b8' }}>𝕏 あり</span>
            )}
          </div>
        </div>

        {/* Status badge */}
        <div style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '6px 12px',
          borderRadius: '8px',
          background: status.bg,
          color: status.color,
          fontSize: '12px', fontWeight: '800',
        }}>
          <span style={{
            width: '6px', height: '6px', borderRadius: '50%',
            background: status.dot, display: 'inline-block',
          }} />
          {status.label}
        </div>
      </div>

      {/* ── Sub actions row ───────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '8px 16px',
        borderTop: '1px solid #f1f5f9',
        background: '#fafbfc',
        flexWrap: 'wrap',
      }}>

        {/* 指名設定 */}
        {showPicker && (
          <button
            onMouseEnter={() => setHovPicker(true)}
            onMouseLeave={() => setHovPicker(false)}
            onClick={() => onOpenPicker(guestId)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '6px 12px',
              border: `1px solid ${hovPicker ? '#6ee7b7' : '#e2e8f0'}`,
              borderRadius: '6px',
              background: hovPicker ? '#ecfdf5' : '#fff',
              color: hovPicker ? '#059669' : '#475569',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            🎯 指名設定
            {picks.length > 0 && (
              <span style={{
                background: '#10b981', color: '#fff',
                borderRadius: '999px', padding: '0 5px',
                fontSize: '10px', fontWeight: '800',
              }}>{picks.length}</span>
            )}
          </button>
        )}

        {/* Priority select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', whiteSpace: 'nowrap' }}>
            優先度
          </span>
          <select
            value={priority}
            onChange={(e) => onUpdatePriority(guestId, parseInt(e.target.value, 10))}
            title="優先保証に設定すると、このゲストの第1希望キャストと優先的にマッチします"
            style={{
              padding: '5px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              background: priority >= 1 ? '#fffbeb' : priority < 0 ? '#f1f5f9' : '#fff',
              color: priority >= 1 ? '#92400e' : priority < 0 ? '#64748b' : '#475569',
              fontSize: '12px', fontWeight: '700',
              cursor: 'pointer', outline: 'none',
            }}
          >
            {PRIORITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* 詳細 */}
        {hasDetail && (
          <button
            onMouseEnter={() => setHovDetail(true)}
            onMouseLeave={() => setHovDetail(false)}
            onClick={() => setShowDetail(v => !v)}
            style={{
              padding: '6px 12px',
              border: `1px solid ${showDetail ? '#6c63ff' : hovDetail ? '#c4b5fd' : '#e2e8f0'}`,
              borderRadius: '6px',
              background: showDetail ? '#f5f3ff' : hovDetail ? '#faf9ff' : '#fff',
              color: showDetail ? '#6c63ff' : hovDetail ? '#6c63ff' : '#475569',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {showDetail ? '▲ 詳細を閉じる' : '▼ 詳細'}
          </button>
        )}

        {/* Delete */}
        <div style={{ marginLeft: 'auto' }}>
          <button
            onMouseEnter={() => setHovDel(true)}
            onMouseLeave={() => setHovDel(false)}
            onClick={async () => {
              if (await confirm({ message: 'ゲストを削除しますか？', type: 'danger' })) onRemove(guestId);
            }}
            style={{
              padding: '6px 12px',
              border: `1px solid ${hovDel ? '#fca5a5' : '#e2e8f0'}`,
              borderRadius: '6px',
              background: hovDel ? '#fef2f2' : '#fff',
              color: hovDel ? '#ef4444' : '#94a3b8',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            削除
          </button>
        </div>
      </div>

      {/* ── Detail panel ─────────────────────────────────────────────── */}
      {showDetail && hasDetail && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #f1f5f9',
          background: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          {guest.vrchat_profile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: 28 }}>VRC</span>
              <a
                href={guest.vrchat_profile}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '13px', color: '#6c63ff', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all' }}
                onMouseEnter={e => { e.target.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.target.style.textDecoration = 'none'; }}
              >
                {guest.vrchat_profile}
              </a>
            </div>
          )}
          {guest.x_url && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: 28 }}>𝕏</span>
              <a
                href={guest.x_url}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: '13px', color: '#6c63ff', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all' }}
                onMouseEnter={e => { e.target.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.target.style.textDecoration = 'none'; }}
              >
                {guest.x_url}
              </a>
            </div>
          )}
          {guest.profile_text && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: 28, paddingTop: 1 }}>📝</span>
              <span style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {guest.profile_text}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GuestList = ({ guests, onRemove, onUpdatePriority, onOpenPicker, matchingMode, showMatchedOnly, stats = {}, canViewHistory = false }) => {
  const confirm = useConfirm();

  if (Object.keys(guests).length === 0) return null;

  const mode       = matchingMode || 'cast_select';
  const showPicker = mode === 'guest_select' || mode === 'both_select';

  const visibleEntries = showMatchedOnly
    ? Object.entries(guests).filter(([, g]) => g.status === 'matched')
    : Object.entries(guests);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
      {visibleEntries.map(([guestId, guest]) => (
        <GuestCard
          key={guestId}
          guestId={guestId}
          guest={guest}
          onRemove={onRemove}
          onUpdatePriority={onUpdatePriority}
          onOpenPicker={onOpenPicker}
          showPicker={showPicker}
          confirm={confirm}
          stat={stats[guestIdentityKey(guest)]}
          canViewHistory={canViewHistory}
        />
      ))}
    </div>
  );
};

export default GuestList;
